import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Grid2 as Grid } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PreviewAnchorLinkField from './fields/PreviewAnchorLinkField';
import { TextField, FormattedTextField, getAnchorRowStyle } from 'framework/ui/field';
import StatusField from './fields/StatusField';
import SourceField from './fields/SourceField';
import CustomTemplateField from './fields/CustomTemplateField';
import { defaultsDeep } from 'lodash';
import { useLocation } from 'react-router-dom';
import { DATE_FORMAT, useDateService, getWithThousandsSeparator } from 'framework/utils';
import { Tooltip } from 'framework/ui/mui-builder';
import moment from 'moment';
import NewDeliveryDateField from './fields/NewDeliveryDateField';
import AdditionalHoursField from './fields/AdditionalHoursField';
import {
  EditableListDataGridLayout,
  EDITABLE_LIST_CUSTOM,
  EDITABLE_LIST_FIELD,
  EDITABLE_LIST_GROUP,
} from 'framework/list';

const ChangeRequestListGrid = ({
  projectId,
  isSensitiveDataIsShown,
}: {
  projectId: string | undefined;
  isSensitiveDataIsShown?: boolean;
}) => {
  const { hash } = useLocation();
  const { datesDifference, getToday } = useDateService();

  return (
    <EditableListDataGridLayout
      rerenderTableCellsDeps={[isSensitiveDataIsShown]}
      rowProps={data =>
        defaultsDeep(getAnchorRowStyle(data, hash), {
          sx: {
            padding: 0,
            px: 1,
          },
        })
      }
      getTableCells={isEditModeLayout =>
        Promise.resolve([
          {
            type: EDITABLE_LIST_GROUP,
            component: Accordion,
            componentProps: {
              sx: {
                background: 'transparent',
              },
            },
            gridCellProps: {
              size: 12,
            },
            children: [
              {
                type: EDITABLE_LIST_GROUP,
                component: AccordionSummary,
                componentProps: {
                  onClick: (event: any) => {
                    event.stopPropagation();
                  },
                  expandIcon: <ExpandMoreIcon />,
                  'aria-controls': 'panel1-content',
                  sx: {
                    p: 0,
                    fontSize: '14px',
                    fontFamily: `var(--open-sans)`,
                    flexDirection: 'row-reverse',
                    '& .MuiAccordionSummary-content': {
                      pointerEvents: 'auto',
                      '&.Mui-expanded': {
                        m: 0,
                      },
                    },
                  },
                },
                gridCellProps: {
                  size: 12,
                },
                children: [
                  {
                    type: EDITABLE_LIST_GROUP,
                    component: Grid,
                    componentProps: {
                      container: true,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    },
                    gridCellProps: {
                      size: 12,
                    },
                    children: [
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'alias',
                        isEditor: false,
                        component: PreviewAnchorLinkField,
                        componentProps: { label: '', idSource: 'id', projectId },
                        gridCellProps: {
                          size: 0.5,
                        },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'title',
                        isEditor: false,
                        component: TextField,
                        componentProps: { label: '', placeholder: 'Description' },
                        gridCellProps: { size: 2.5, sx: { '.MuiFormControl-root': { mb: 0 } } },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'createdAt',
                        isEditor: false,
                        component: FormattedTextField,
                        componentProps: {
                          label: '',
                          value: (str: string) => (
                            <Tooltip title={moment(str).format(DATE_FORMAT.API_ONLY_DATE)}>
                              <span>{datesDifference(getToday(), str)}</span>
                            </Tooltip>
                          ),
                        },
                        gridCellProps: { size: 1.5, sx: { '.MuiFormControl-root': { mb: 0 } } },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'additionalFte',
                        isEditor: false,
                        component: FormattedTextField,
                        componentProps: { label: '', format: (str: string) => (str ? `${str} FTE` : '—') },
                        gridCellProps: { size: 1, sx: { '.MuiFormControl-root': { mb: 0 } } },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'additionalHours',
                        isEditor: false,
                        component: AdditionalHoursField,
                        componentProps: { label: '' },
                        gridCellProps: { size: 1, sx: { '.MuiFormControl-root': { mb: 0 } } },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'additionalBudgetAmount',
                        isEditor: false,
                        component: FormattedTextField,
                        componentProps: {
                          label: '',
                          value: isSensitiveDataIsShown ? undefined : '*****',
                          format: (str: string) =>
                            isSensitiveDataIsShown && str ? `${getWithThousandsSeparator(str)} USD` : str,
                        },
                        gridCellProps: { size: 1.5, sx: { '.MuiFormControl-root': { mb: 0 } } },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'newDeliveryDate',
                        isEditor: false,
                        component: NewDeliveryDateField,
                        componentProps: {
                          label: '',
                        },
                        gridCellProps: { size: 1.5, sx: { '.MuiFormControl-root': { mb: 0 } } },
                      },
                      {
                        type: EDITABLE_LIST_FIELD,
                        source: 'status',
                        isEditor: false,
                        component: StatusField,
                        gridCellProps: { size: 1, sx: { '.MuiFormControl-root': { mb: 0 } } },
                      },
                      {
                        type: EDITABLE_LIST_CUSTOM,
                        isEditor: false,
                        component: SourceField,
                        componentProps: {
                          statusSource: 'status',
                          idSource: 'id',
                          afsArtifactId: 'afsArtifactId',
                          projectId,
                        },
                        gridCellProps: {
                          size: 1,
                          sx: { '.MuiFormControl-root': { mb: 0 }, display: 'flex', justifyContent: 'end' },
                        },
                      },
                    ],
                  },
                ],
              },
              {
                type: EDITABLE_LIST_GROUP,
                component: AccordionDetails,
                componentProps: { p: 0 },
                gridCellProps: {
                  size: 12,
                },
                children: [
                  {
                    type: EDITABLE_LIST_FIELD,
                    source: 'timeline',
                    isEditor: false,
                    component: CustomTemplateField,
                    componentProps: { label: '' },
                    gridCellProps: {
                      size: 12,
                    },
                  },
                ],
              },
            ],
          },
        ])
      }
    />
  );
};

export default ChangeRequestListGrid;
